import {Fragment, type ReactNode} from 'react'
import {useLocation} from 'react-router-dom'
import {type LucideIcon} from 'lucide-react'
import {cn} from '@/lib/utils'
import {RefreshingNavLink} from '@/components/ui/refreshing-nav-link'
import {SidebarNavTestIds, type SidebarNavId} from '@/testids/SidebarNavTestIds'

export interface SidebarNavItem {
    /**
     * Static items use {@code SidebarNavId} enum entries (type-safe, mirrors
     * the backend test-id registry). Dynamic items — currently only the
     * promoted-types injection from issue 301 — pass a runtime string in the
     * format {@code "promoted-{tableName}-{code}"} so the testid stays
     * unique without bloating the static enum.
     */
    id: SidebarNavId | (string & {})
    title: string
    description: string
    href: string
    icon: LucideIcon
    badge?: ReactNode
    end?: boolean
    /**
     * Suppress the active state when {@code location.pathname} contains this
     * substring. Used by parent list-page items so they yield to a more
     * specific sibling — e.g. the Parties item passes {@code '/by-type/'} so
     * the promoted Teams item is the only one highlighted on
     * {@code /parties/by-type/TEAM} (issue 680).
     */
    excludePathContains?: string
    /** Optional section header to render before this item. Same value on
     *  consecutive items collapses into a single header. */
    section?: string
}

interface SidebarNavProps {
    items: SidebarNavItem[]
    /**
     * Accessible name for the navigation landmark. Required so screen-reader
     * users can distinguish multiple `<nav>` landmarks on the page
     * (header utility menu vs sidebar). Issue 800.
     */
    'aria-label': string
}

// Keyboard navigation pattern decision (resolves Gap G-04 from test concept):
// Standard Tab navigation is used — each nav item is focusable via Tab in sequence.
// This is consistent with how nav menus behave outside toolbar containers (WCAG 2.4.3).
// Arrow-key roving tabindex is not used here; the SidebarNav is a <nav> landmark, not a toolbar.
export function SidebarNav({items, 'aria-label': ariaLabel}: SidebarNavProps) {
    const location = useLocation()

    let lastSection: string | undefined

    return (
        <nav aria-label={ariaLabel} className="w-64 shrink-0">
            <ul className="space-y-2">
                {items.map((item) => {
                    const showSectionHeader = item.section !== undefined && item.section !== lastSection
                    if (item.section !== undefined) {
                        lastSection = item.section
                    }
                    // NavLink matches against the path part of `to` only, so strip query
                    // and hash before comparing — otherwise items whose href carries a
                    // default filter (e.g. `?status=DRAFT,PENDING`) get aria-current="page"
                    // from NavLink while our local isActive flag returns false, leaving
                    // the active link with low-contrast description text.
                    const hrefPath = item.href.split('?')[0].split('#')[0]
                    const baseMatch = item.end
                        ? location.pathname === hrefPath
                        : location.pathname.startsWith(hrefPath)
                    const excluded = item.excludePathContains !== undefined
                        && location.pathname.includes(item.excludePathContains)
                    const isActive = baseMatch && !excluded
                    return (
                        <Fragment key={item.href}>
                            {showSectionHeader && (
                                <li className="pt-3 first:pt-0 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                                    aria-hidden="true">
                                    {item.section}
                                </li>
                            )}
                        <li>
                            <RefreshingNavLink
                                to={item.href}
                                end={item.end}
                                aria-current={isActive ? 'page' : 'false'}
                                aria-label={String(item.title)}
                                // eslint-disable-next-line i18next/no-literal-string -- ARIA reference IDs are technical, not user-visible.
                                aria-describedby={`sidebar-nav-desc-${item.id}`}
                                className={cn(
                                    // WHY: no transition-colors — the cross-fade from transparent
                                    // to bg-primary leaves the description child rendering with
                                    // ~3% alpha primary background mid-transition, which axe-core
                                    // flags as a WCAG 4.5:1 color-contrast failure on the static
                                    // page scan (issue 420 Group D).
                                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
                                    isActive
                                        ? 'bg-primary text-primary-foreground'
                                        : 'hover:bg-muted'
                                )}
                                data-testid={`${SidebarNavTestIds.linkNavPrefix()}${item.id}`}
                            >
                                <item.icon className="h-4 w-4"/>
                                <div className="flex-1">
                                    <div className="font-medium flex items-center gap-2">
                                        {item.title}
                                        {item.badge}
                                    </div>
                                    {/* Show the description on every item, not only inactive
                                        ones — the layout shifted when items became active and the
                                        user lost the context they had just clicked. */}
                                    <div
                                        // eslint-disable-next-line i18next/no-literal-string -- DOM id is technical, not user-visible.
                                        id={`sidebar-nav-desc-${item.id}`}
                                        className={cn(
                                            'text-xs',
                                            isActive ? 'text-primary-foreground' : 'text-muted-foreground',
                                        )}>
                                        {item.description}
                                    </div>
                                </div>
                            </RefreshingNavLink>
                        </li>
                        </Fragment>
                    )
                })}
            </ul>
        </nav>
    )
}
